/**
 * ModelPlannerPage - Full-page wizard for completing model planner questions
 *
 * Opens in a new tab, full-screen experience for the actual planner workflow.
 * Loads config from database and persists answers to the specific project.
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  GitBranch,
  Cloud,
  CloudOff,
  Plus,
  Trash2,
  Edit3,
} from "lucide-react";
import PlannerConfigService, {
  NAV_TOKENS,
} from "../../services/plannerConfigService";
import ModelPlannerService from "../../services/modelPlannerService";
import modelPlannerExportService from "../../services/modelPlannerExportService";
import { supabase } from "../../lib/supabase";
import { formatJobRevNumber } from "../../utils/projectFormatting";
import ExportButton from "./ExportButton";
import ManagerReviewPanel from "./ManagerReviewPanel";
import { computeFlags } from "../../config/managerReviewFlags";

/**
 * Build a navigation tree from questions
 */
const buildNavigationTree = (
  section,
  answers = {},
  sectionKey = "",
  completedQuestions = new Set(),
  itemIndex = null,
  startFromLoopQuestion = false,
) => {
  if (!section?.questions || !section?.initialQuestion) return [];

  const questions = section.questions;
  const questionOrder = section.questionOrder || Object.keys(questions);
  const visited = new Set();

  // For looped items, find the first question inside the loop (skip initial "has_X" question)
  const getLoopStartQuestion = () => {
    const initialQ = section.initialQuestion;
    // Find first question that's not the initial "has_X" or "are_there_X" question
    return (
      questionOrder.find(
        (qId) =>
          qId !== initialQ &&
          !qId.startsWith("are_there_") &&
          !qId.startsWith("has_") &&
          questions[qId],
      ) || questionOrder.find((qId) => qId !== initialQ && questions[qId])
    );
  };

  const getAnswer = (questionId) => {
    const question = questions[questionId];
    if (!question) return undefined;
    const field = question.field || questionId;
    // For looped sections with an item index, use item-indexed key format
    const answerKey =
      itemIndex !== null
        ? `${sectionKey}.${itemIndex}.${questionId}.${field}`
        : `${sectionKey}.${questionId}.${field}`;
    return answers[answerKey];
  };

  const isCompleted = (questionId) => {
    // For looped sections with an item index, check item-indexed completion key
    const completionKey =
      itemIndex !== null
        ? `${sectionKey}.${itemIndex}.${questionId}`
        : `${sectionKey}.${questionId}`;
    return completedQuestions.has(completionKey);
  };

  const resolveLoopStart = (token) => {
    if (token === "LOOP_START_SINGLE" || token === "ADA_LOOP_START_SINGLE") {
      const initialQ = section.initialQuestion;
      const nextQuestion =
        questionOrder.find(
          (qId) =>
            qId !== initialQ &&
            !qId.startsWith("are_there_") &&
            !qId.startsWith("has_") &&
            questions[qId],
        ) || questionOrder.find((qId) => qId !== initialQ && questions[qId]);
      return nextQuestion || null;
    }
    return null;
  };

  const buildNode = (questionId, branchLabel = null) => {
    if (NAV_TOKENS.includes(questionId)) {
      const resolved = resolveLoopStart(questionId);
      if (resolved) {
        return buildNode(resolved, branchLabel);
      }
      return null;
    }

    if (!questionId || visited.has(questionId)) {
      return null;
    }

    const question = questions[questionId];
    if (!question) return null;

    visited.add(questionId);

    const node = {
      id: questionId,
      question,
      branchLabel,
      children: [],
    };

    if (!isCompleted(questionId)) {
      return node;
    }

    if (question.type === "boolean" || question.type === "manager_decision") {
      const answer = getAnswer(questionId);
      if (answer === true) {
        const yesChild = buildNode(question.onYes, "Yes");
        if (yesChild) node.children.push(yesChild);
      } else if (answer === false) {
        const noChild = buildNode(question.onNo, "No");
        if (noChild) node.children.push(noChild);
      }
    } else if (question.next) {
      const nextChild = buildNode(question.next);
      if (nextChild) node.children.push(nextChild);
    }

    return node;
  };

  // For looped items, start from the first question inside the loop
  const startQuestion = startFromLoopQuestion
    ? getLoopStartQuestion()
    : section.initialQuestion;
  const root = buildNode(startQuestion);
  return root ? [root] : [];
};

const findPathToQuestion = (nodes, targetId, currentPath = []) => {
  for (const node of nodes) {
    const newPath = [...currentPath, node.id];
    if (node.id === targetId) {
      return newPath;
    }
    if (node.children?.length > 0) {
      const found = findPathToQuestion(node.children, targetId, newPath);
      if (found) return found;
    }
  }
  return null;
};

/**
 * TreeNode - Recursive component to render a navigation tree node
 */
const TreeNode = ({
  node,
  depth = 0,
  currentQuestionId,
  expandedNodes,
  onToggleExpand,
  onNavigate,
  completedQuestions,
  sectionKey,
  itemIndex = null,
  pathToCurrentQuestion,
}) => {
  const isCurrentQuestion = node.id === currentQuestionId;
  // For looped sections, use itemIndex in the key; otherwise just sectionKey.questionId
  const questionKey =
    itemIndex !== null
      ? `${sectionKey}.${itemIndex}.${node.id}`
      : `${sectionKey}.${node.id}`;
  const isCompleted = completedQuestions.has(questionKey);
  const hasChildren = node.children?.length > 0;
  // Auto-expand completed questions so users can see and navigate their full path
  const isExpanded = expandedNodes.has(node.id) || isCompleted;
  const isOnPath = pathToCurrentQuestion?.includes(node.id);
  const isBranching =
    node.question?.type === "boolean" ||
    node.question?.type === "manager_decision";

  return (
    <div style={{ marginLeft: depth > 0 ? 12 : 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          position: "relative",
        }}
      >
        {depth > 0 && (
          <div
            style={{
              position: "absolute",
              left: -8,
              top: 0,
              bottom: "50%",
              width: 1,
              background: "var(--gray-700)",
            }}
          />
        )}

        {node.branchLabel && (
          <span
            style={{
              fontSize: "9px",
              padding: "1px 4px",
              borderRadius: 3,
              background:
                node.branchLabel === "Yes"
                  ? "rgba(34, 197, 94, 0.2)"
                  : "rgba(239, 68, 68, 0.2)",
              color: node.branchLabel === "Yes" ? "#22c55e" : "#ef4444",
              marginRight: 2,
            }}
          >
            {node.branchLabel}
          </span>
        )}

        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(node.id);
            }}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "var(--gray-500)",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ChevronDown
              size={12}
              style={{
                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.15s",
              }}
            />
          </button>
        ) : (
          <span style={{ width: 12 }} />
        )}

        <button
          onClick={() => onNavigate(node.id)}
          style={{
            flex: 1,
            padding: "3px 6px",
            background: isCurrentQuestion ? "var(--gray-700)" : "transparent",
            border: "none",
            borderRadius: "var(--radius-sm)",
            textAlign: "left",
            cursor: "pointer",
            fontSize: "11px",
            color: isCompleted
              ? "#22c55e"
              : isCurrentQuestion
                ? "var(--gray-100)"
                : isOnPath
                  ? "var(--gray-300)"
                  : "var(--gray-500)",
            display: "flex",
            alignItems: "center",
            gap: 4,
            overflow: "hidden",
          }}
        >
          {isCompleted && <Check size={10} style={{ flexShrink: 0 }} />}
          {isBranching && !isCompleted && (
            <GitBranch size={10} style={{ flexShrink: 0, opacity: 0.5 }} />
          )}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {node.id}
          </span>
        </button>
      </div>

      {hasChildren && isExpanded && (
        <div
          style={{
            borderLeft: "1px solid var(--gray-700)",
            marginLeft: 6,
            paddingLeft: 4,
            marginTop: 2,
          }}
        >
          {node.children.map((child, i) => (
            <TreeNode
              key={`${child.id}-${i}`}
              node={child}
              depth={depth + 1}
              currentQuestionId={currentQuestionId}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
              onNavigate={onNavigate}
              completedQuestions={completedQuestions}
              sectionKey={sectionKey}
              itemIndex={itemIndex}
              pathToCurrentQuestion={pathToCurrentQuestion}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * ItemManager - Manages multiple items in a looped section (ponds, curbs, walls, etc.)
 */
const ItemManager = ({
  sectionKey,
  sectionLabel,
  items,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onUpdateItemName,
  onContinue,
  onBack,
  canGoBack,
  getItemName,
}) => {
  const hasItems = items && items.length > 0;
  const [isAdding, setIsAdding] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingName, setEditingName] = useState("");
  const inputRef = useRef(null);
  const editInputRef = useRef(null);

  // Focus input when adding starts
  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  // Focus input when editing starts
  useEffect(() => {
    if (editingIndex !== null && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingIndex]);

  const handleAddWithName = () => {
    if (newItemName.trim()) {
      onAddItem(newItemName.trim());
      setNewItemName("");
      setIsAdding(false);
    }
  };

  const handleSaveEdit = (index) => {
    if (editingName.trim()) {
      onUpdateItemName(index, editingName.trim());
    }
    setEditingIndex(null);
    setEditingName("");
  };

  const singularLabel = sectionLabel.replace(/s$/, "").replace(/\s*\(.*\)/, "");

  return (
    <div
      style={{
        background: "var(--dark-surface)",
        border: "1px solid var(--gray-700)",
        borderRadius: "var(--radius-lg)",
        padding: "var(--space-6)",
        maxWidth: 600,
      }}
    >
      <h2
        style={{
          margin: 0,
          marginBottom: "var(--space-4)",
          fontSize: "var(--text-xl)",
          fontWeight: 500,
          color: "var(--gray-100)",
        }}
      >
        {sectionLabel}
      </h2>

      {hasItems ? (
        <div style={{ marginBottom: "var(--space-4)" }}>
          <div
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--gray-400)",
              marginBottom: "var(--space-2)",
            }}
          >
            {items.length} item{items.length !== 1 ? "s" : ""} added
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-2)",
            }}
          >
            {items.map((item, index) => {
              const itemName = getItemName(item, index);
              const isComplete = item.completed;
              const isEditingThis = editingIndex === index;
              return (
                <div
                  key={item.id || index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-3)",
                    padding: "var(--space-3)",
                    background: "var(--gray-800)",
                    borderRadius: "var(--radius-md)",
                    border: isComplete
                      ? "1px solid rgba(34, 197, 94, 0.3)"
                      : "1px solid var(--gray-700)",
                  }}
                >
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: isComplete
                        ? "rgba(34, 197, 94, 0.2)"
                        : "var(--gray-700)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {isComplete ? (
                      <Check size={14} style={{ color: "#22c55e" }} />
                    ) : (
                      <span
                        style={{
                          fontSize: "var(--text-xs)",
                          color: "var(--gray-500)",
                        }}
                      >
                        {index + 1}
                      </span>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {isEditingThis ? (
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveEdit(index);
                          if (e.key === "Escape") {
                            setEditingIndex(null);
                            setEditingName("");
                          }
                        }}
                        onBlur={() => handleSaveEdit(index)}
                        style={{
                          width: "100%",
                          padding: "var(--space-1) var(--space-2)",
                          background: "var(--gray-700)",
                          border: "1px solid var(--blue-500)",
                          borderRadius: "var(--radius-sm)",
                          color: "var(--gray-100)",
                          fontSize: "var(--text-sm)",
                          fontWeight: 500,
                        }}
                      />
                    ) : (
                      <div
                        onClick={() => {
                          setEditingIndex(index);
                          setEditingName(itemName || `Item ${index + 1}`);
                        }}
                        style={{
                          color: "var(--gray-200)",
                          fontSize: "var(--text-sm)",
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          cursor: "text",
                          padding: "var(--space-1) 0",
                        }}
                        title="Click to rename"
                      >
                        {itemName || `Item ${index + 1}`}
                      </div>
                    )}
                    {!isComplete && !isEditingThis && (
                      <div
                        style={{
                          fontSize: "var(--text-xs)",
                          color: "var(--amber-400)",
                        }}
                      >
                        Incomplete
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => onEditItem(index)}
                    style={{
                      background: "rgba(59, 130, 246, 0.15)",
                      border: "1px solid rgba(59, 130, 246, 0.3)",
                      borderRadius: "var(--radius-sm)",
                      padding: "var(--space-1) var(--space-2)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-1)",
                      color: "var(--blue-400)",
                      fontSize: "var(--text-xs)",
                      fontWeight: 500,
                    }}
                    title="Edit details"
                  >
                    <Edit3 size={12} />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          `Delete "${itemName || `Item ${index + 1}`}"?`,
                        )
                      ) {
                        onDeleteItem(index);
                      }
                    }}
                    style={{
                      background: "rgba(239, 68, 68, 0.1)",
                      border: "none",
                      borderRadius: "var(--radius-sm)",
                      padding: "var(--space-2)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      color: "var(--red-400)",
                    }}
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div
          style={{
            padding: "var(--space-6)",
            background: "var(--gray-800)",
            borderRadius: "var(--radius-md)",
            textAlign: "center",
            marginBottom: "var(--space-4)",
          }}
        >
          <div
            style={{ color: "var(--gray-400)", marginBottom: "var(--space-2)" }}
          >
            No items added yet
          </div>
          <div style={{ color: "var(--gray-500)", fontSize: "var(--text-sm)" }}>
            Add items or continue if there are none
          </div>
        </div>
      )}

      {/* Inline add input */}
      {isAdding && (
        <div
          style={{
            display: "flex",
            gap: "var(--space-2)",
            marginBottom: "var(--space-3)",
            padding: "var(--space-3)",
            background: "var(--gray-800)",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--green-600)",
          }}
        >
          <input
            ref={inputRef}
            type="text"
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddWithName();
              if (e.key === "Escape") {
                setIsAdding(false);
                setNewItemName("");
              }
            }}
            placeholder={`Enter ${singularLabel.toLowerCase()} name...`}
            style={{
              flex: 1,
              padding: "var(--space-2) var(--space-3)",
              background: "var(--gray-700)",
              border: "1px solid var(--gray-600)",
              borderRadius: "var(--radius-sm)",
              color: "var(--gray-100)",
              fontSize: "var(--text-sm)",
            }}
          />
          <button
            onClick={handleAddWithName}
            disabled={!newItemName.trim()}
            style={{
              padding: "var(--space-2) var(--space-3)",
              background: newItemName.trim()
                ? "var(--green-600)"
                : "var(--gray-600)",
              border: "none",
              borderRadius: "var(--radius-sm)",
              color: "white",
              cursor: newItemName.trim() ? "pointer" : "not-allowed",
              fontWeight: 500,
              fontSize: "var(--text-sm)",
            }}
          >
            Add
          </button>
          <button
            onClick={() => {
              setIsAdding(false);
              setNewItemName("");
            }}
            style={{
              padding: "var(--space-2)",
              background: "var(--gray-700)",
              border: "none",
              borderRadius: "var(--radius-sm)",
              color: "var(--gray-400)",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      )}

      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        {canGoBack && (
          <button
            onClick={onBack}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-1)",
              padding: "var(--space-3) var(--space-4)",
              background: "var(--gray-700)",
              border: "none",
              borderRadius: "var(--radius-md)",
              color: "var(--gray-300)",
              cursor: "pointer",
            }}
          >
            <ChevronLeft size={16} />
            Back
          </button>
        )}
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
              padding: "var(--space-3) var(--space-5)",
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              border: "none",
              borderRadius: "var(--radius-md)",
              color: "white",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "var(--text-sm)",
              boxShadow:
                "0 2px 8px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow =
                "0 4px 12px rgba(16, 185, 129, 0.4), inset 0 1px 0 rgba(255,255,255,0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 2px 8px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255,255,255,0.1)";
            }}
          >
            <Plus size={18} strokeWidth={2.5} />
            Add {singularLabel}
          </button>
        )}
        <button
          onClick={onContinue}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-2)",
            padding: "var(--space-3) var(--space-4)",
            background: "var(--cta-blue)",
            border: "none",
            borderRadius: "var(--radius-md)",
            color: "white",
            cursor: "pointer",
            fontWeight: 500,
            marginLeft: "auto",
          }}
        >
          Continue
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

const ModelPlannerPage = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [config, setConfig] = useState(null);
  const [session, setSession] = useState(null);
  const [savedAnswers, setSavedAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set document title when project loads
  useEffect(() => {
    if (project) {
      const jobNum = project.job_number
        ? `${project.job_number}${project.rev ? `-${project.rev}` : ""}`
        : "";
      document.title = jobNum
        ? `${jobNum} - ${project.name} | Planner`
        : `${project.name} | Planner`;
    } else {
      document.title = "Model Planner";
    }
    return () => {
      document.title = "AB Civil Portal"; // Reset on unmount
    };
  }, [project]);

  useEffect(() => {
    const loadData = async () => {
      if (!projectId) {
        setError("No project ID provided");
        setLoading(false);
        return;
      }

      try {
        // Load project info
        const { data: projectData, error: projectError } = await supabase
          .from("projects")
          .select("id, name, job_number, rev")
          .eq("id", projectId)
          .single();

        if (projectError) throw new Error("Project not found");
        setProject(projectData);

        // Load planner config
        PlannerConfigService.invalidateCache();
        const configData = await PlannerConfigService.loadConfiguration();
        const { _versionId, _fromDatabase, ...cleanConfig } = configData;
        setConfig(cleanConfig);

        // Load or create session
        const { data: sessionData, error: sessionError } =
          await ModelPlannerService.getOrCreateSession(parseInt(projectId));
        if (sessionError) {
          console.error("Failed to get/create session:", sessionError);
        } else {
          setSession(sessionData);

          if (sessionData?.id) {
            // Load planner data directly to get proper item structure
            const { data: plannerData } =
              await ModelPlannerService.getPlannerData(sessionData.id);
            if (plannerData?.sections) {
              const mergedAnswers = {};
              const loopedSections = new Set([
                "ponds",
                "curb",
                "walls",
                "ada-ramps",
              ]);

              // Convert nested item-based structure to flat indexed keys
              Object.entries(plannerData.sections).forEach(
                ([sectionKey, items]) => {
                  if (Array.isArray(items)) {
                    items.forEach((item, itemIndex) => {
                      if (item.answers) {
                        Object.entries(item.answers).forEach(
                          ([answerKey, value]) => {
                            if (loopedSections.has(sectionKey)) {
                              // For looped sections: insert item index into key
                              // Convert "curb.has_curb.has_curb" to "curb.0.has_curb.has_curb"
                              const parts = answerKey.split(".");
                              if (parts[0] === sectionKey) {
                                const newKey = `${sectionKey}.${itemIndex}.${parts.slice(1).join(".")}`;
                                mergedAnswers[newKey] = value;
                              } else {
                                mergedAnswers[
                                  `${sectionKey}.${itemIndex}.${answerKey}`
                                ] = value;
                              }
                            } else {
                              // For single sections: use key as-is
                              mergedAnswers[answerKey] = value;
                            }
                          },
                        );
                      }
                    });
                  }
                },
              );
              setSavedAnswers(mergedAnswers);
            }
          }
        }
      } catch (err) {
        console.error("Failed to load data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [projectId]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "var(--dark-bg)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--gray-400)",
        }}
      >
        Loading planner...
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "var(--dark-bg)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "var(--space-4)",
          color: "var(--red-400)",
        }}
      >
        <div>Error: {error}</div>
        <button
          onClick={() => window.close()}
          style={{
            padding: "var(--space-2) var(--space-4)",
            background: "var(--gray-700)",
            border: "none",
            borderRadius: "var(--radius-md)",
            color: "var(--gray-300)",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <PlannerWizard
      config={config}
      session={session}
      savedAnswers={savedAnswers}
      project={project}
      projectId={parseInt(projectId)}
    />
  );
};

/**
 * PlannerWizard - The actual wizard interface
 */
const PlannerWizard = ({
  config,
  session,
  savedAnswers,
  project,
  projectId,
}) => {
  const sectionKeys = useMemo(
    () => Object.keys(config?.sections || {}),
    [config],
  );

  // Determine which sections are looped vs single
  const loopedSections = useMemo(() => {
    const looped = new Set();
    sectionKeys.forEach((key) => {
      const section = config?.sections?.[key];
      if (section?.type === "looped") {
        looped.add(key);
      }
    });
    return looped;
  }, [config, sectionKeys]);

  const [currentSectionKey, setCurrentSectionKey] = useState(
    sectionKeys[0] || "",
  );
  // Start with null for looped sections (item manager mode), otherwise initialQuestion
  const [currentQuestionId, setCurrentQuestionId] = useState(() => {
    const firstSection = sectionKeys[0];
    const section = config?.sections?.[firstSection];
    if (section?.type === "looped") {
      return null; // Item manager mode
    }
    return section?.initialQuestion || null;
  });
  const [currentItemIndex, setCurrentItemIndex] = useState(null); // Which item we're editing
  const [sectionItems, setSectionItems] = useState({}); // { ponds: [{id, answers, completed}, ...], curb: [...] }
  const [answers, setAnswers] = useState(savedAnswers || {});
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [completedQuestions, setCompletedQuestions] = useState(new Set());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState(
    new Set([sectionKeys[0]]),
  );
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [saveStatus, setSaveStatus] = useState("idle");
  const saveTimeoutRef = useRef(null);

  // Check if current section is looped
  const isLoopedSection = loopedSections.has(currentSectionKey);
  const isInItemManagerMode = isLoopedSection && currentQuestionId === null;

  // Get first question for a looped section (after the "are there any..." question)
  const getFirstLoopQuestion = useCallback(
    (sectionKey) => {
      const section = config?.sections?.[sectionKey];
      if (!section) return null;
      const questions = section.questions || {};
      const questionIds = section.questionOrder || Object.keys(questions);
      const initialQ = section.initialQuestion;
      // Find first question that's not the initial "are there any" question
      return (
        questionIds.find(
          (qId) =>
            qId !== initialQ &&
            !qId.startsWith("are_there_") &&
            !qId.startsWith("has_") &&
            questions[qId],
        ) || questionIds.find((qId) => qId !== initialQ && questions[qId])
      );
    },
    [config],
  );

  // Get display name for an item in a looped section
  const getItemDisplayName = useCallback(
    (sectionKey, itemIndex) => {
      // Map section keys to the field that holds the item name
      const nameFields = {
        ponds: "pond_name.pond_name",
        curb: "curb_type_name.curb_type_name",
        walls: "wall_type_name.wall_type_name",
        "ada-ramps": "ada_ramp_name.ada_ramp_name",
      };

      const nameField = nameFields[sectionKey];
      if (!nameField) return null;

      // Build the answer key
      const answerKey = `${sectionKey}.${itemIndex}.${nameField}`;
      const name = answers[answerKey];

      return name || null;
    },
    [answers],
  );

  // Initialize: parse savedAnswers to extract items for looped sections
  useEffect(() => {
    if (Object.keys(savedAnswers || {}).length > 0) {
      const completed = new Set();
      const items = {};

      // Parse answers to find items
      Object.keys(savedAnswers).forEach((key) => {
        const parts = key.split(".");
        if (parts.length >= 3) {
          const sectionKey = parts[0];
          // Check if second part is a number (item index)
          const maybeIndex = parseInt(parts[1], 10);
          if (!isNaN(maybeIndex) && loopedSections.has(sectionKey)) {
            // This is an item-based answer: section.itemIndex.questionId.field
            if (!items[sectionKey]) items[sectionKey] = [];
            while (items[sectionKey].length <= maybeIndex) {
              items[sectionKey].push({
                id: `item-${items[sectionKey].length}`,
                answers: {},
                completed: false,
              });
            }
            // Store the answer in the item
            const itemKey = parts.slice(2).join(".");
            items[sectionKey][maybeIndex].answers[itemKey] = savedAnswers[key];
            // Mark question as completed for looped sections: section.itemIndex.questionId
            const questionId = parts[2];
            completed.add(`${sectionKey}.${maybeIndex}.${questionId}`);
          } else {
            // Old format or single section: section.questionId.field
            completed.add(`${parts[0]}.${parts[1]}`);
          }
        }
      });

      // Check completion status for items
      Object.entries(items).forEach(([sectionKey, sectionItemList]) => {
        sectionItemList.forEach((item, index) => {
          // Check if this item has a confirmation answer (indicating completion)
          const hasConfirmation = Object.keys(item.answers).some(
            (k) => k.includes("confirmation") || k.includes("add_another"),
          );
          if (hasConfirmation) {
            item.completed = true;
          }
        });
      });

      setSectionItems(items);
      setCompletedQuestions(completed);

      // Auto-expand sections that have items
      const sectionsWithItems = Object.keys(items).filter(
        (k) => items[k].length > 0,
      );
      if (sectionsWithItems.length > 0) {
        setExpandedSections((prev) => {
          const next = new Set(prev);
          sectionsWithItems.forEach((k) => next.add(k));
          return next;
        });
      }

      // Auto-expand all completed question nodes so tree doesn't collapse
      // Extract question IDs from completed keys
      const completedNodeIds = new Set();
      completed.forEach((key) => {
        const parts = key.split(".");
        // Key format: section.questionId or section.itemIndex.questionId
        const qId = parts.length >= 3 ? parts[2] : parts[1];
        completedNodeIds.add(qId);
      });
      if (completedNodeIds.size > 0) {
        setExpandedNodes((prev) => {
          const next = new Set(prev);
          completedNodeIds.forEach((id) => next.add(id));
          return next;
        });
      }
    }
  }, [savedAnswers, loopedSections]);

  const saveAnswersToDatabase = useCallback(
    async (newAnswers, itemsState = null) => {
      if (!session?.id) return;

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      setSaveStatus("saving");

      saveTimeoutRef.current = setTimeout(async () => {
        try {
          // Build the nested structure the database expects
          const { data: existingData } =
            await ModelPlannerService.getPlannerData(session.id);
          const plannerData = existingData || { sections: {} };
          if (!plannerData.sections) plannerData.sections = {};

          // Group answers by section and item index
          Object.entries(newAnswers).forEach(([key, value]) => {
            const parts = key.split(".");
            const sectionKey = parts[0];

            if (loopedSections.has(sectionKey)) {
              // Looped section: key format is "section.itemIndex.questionId.field"
              const itemIndex = parseInt(parts[1], 10);
              if (!isNaN(itemIndex)) {
                // Initialize section array if needed
                if (!plannerData.sections[sectionKey]) {
                  plannerData.sections[sectionKey] = [];
                }
                // Ensure we have enough items
                while (plannerData.sections[sectionKey].length <= itemIndex) {
                  plannerData.sections[sectionKey].push({
                    id: `item-${Date.now()}-${plannerData.sections[sectionKey].length}`,
                    order: plannerData.sections[sectionKey].length,
                    answers: {},
                    completed: false,
                  });
                }
                // Store the answer in the original format (without item index)
                const originalKey = `${sectionKey}.${parts.slice(2).join(".")}`;
                plannerData.sections[sectionKey][itemIndex].answers[
                  originalKey
                ] = value;
              }
            } else {
              // Single section: key format is "section.questionId.field"
              if (!plannerData.sections[sectionKey]) {
                plannerData.sections[sectionKey] = [
                  {
                    id: `item-${Date.now()}`,
                    order: 0,
                    answers: {},
                    completed: false,
                  },
                ];
              }
              plannerData.sections[sectionKey][0].answers[key] = value;
            }
          });

          // Update item completion status from itemsState if provided
          if (itemsState) {
            Object.entries(itemsState).forEach(([sectionKey, items]) => {
              if (plannerData.sections[sectionKey]) {
                items.forEach((item, index) => {
                  if (plannerData.sections[sectionKey][index]) {
                    plannerData.sections[sectionKey][index].completed =
                      item.completed;
                  }
                });
              }
            });
          }

          await ModelPlannerService.updatePlannerData(session.id, plannerData);

          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 2000);
        } catch (error) {
          console.error("Error saving answers:", error);
          setSaveStatus("error");
        }
      }, 150); // Reduced from 500ms for faster feedback
    },
    [session?.id, loopedSections],
  );

  const currentSection = config?.sections?.[currentSectionKey];
  const currentQuestion = currentSection?.questions?.[currentQuestionId];

  // Calculate progress for each section (for progress indicators)
  const getSectionProgress = useCallback(
    (sectionKey) => {
      const section = config?.sections?.[sectionKey];
      if (!section) return { completed: 0, total: 0, isLooped: false };

      const isLooped = loopedSections.has(sectionKey);
      const items = sectionItems[sectionKey] || [];

      if (isLooped) {
        const completedItems = items.filter((i) => i.completed).length;
        return {
          completed: completedItems,
          total: items.length,
          isLooped: true,
        };
      } else {
        // Count completed questions for this section
        let completed = 0;
        completedQuestions.forEach((key) => {
          if (key.startsWith(`${sectionKey}.`)) completed++;
        });
        const total = Object.keys(section.questions || {}).length;
        return { completed, total, isLooped: false };
      }
    },
    [config, loopedSections, sectionItems, completedQuestions],
  );

  const sectionTrees = useMemo(() => {
    const trees = {};
    sectionKeys.forEach((key) => {
      trees[key] = buildNavigationTree(
        config?.sections?.[key],
        answers,
        key,
        completedQuestions,
      );
    });
    return trees;
  }, [config, sectionKeys, answers, completedQuestions]);

  const pathToCurrentQuestion = useMemo(() => {
    const tree = sectionTrees[currentSectionKey];
    if (!tree?.length) return [];
    return findPathToQuestion(tree, currentQuestionId) || [];
  }, [sectionTrees, currentSectionKey, currentQuestionId]);

  useEffect(() => {
    const tree = sectionTrees[currentSectionKey];
    if (!tree?.length) return;

    // Find path to current question
    const path = findPathToQuestion(tree, currentQuestionId) || [];

    // Collect ALL completed question IDs to keep their branches expanded
    const completedIds = new Set();
    completedQuestions.forEach((key) => {
      const parts = key.split(".");
      // Extract question ID from key format: section.questionId or section.itemIndex.questionId
      const qId = parts.length >= 3 ? parts[2] : parts[1];
      completedIds.add(qId);
    });

    setExpandedNodes((prev) => {
      const next = new Set(prev);
      // Always expand path to current question
      path.forEach((id) => next.add(id));
      // Keep all completed questions expanded so tree doesn't collapse
      completedIds.forEach((id) => next.add(id));
      return next;
    });

    // Ensure current section is expanded
    setExpandedSections((prev) => {
      if (prev.has(currentSectionKey)) return prev;
      const next = new Set(prev);
      next.add(currentSectionKey);
      return next;
    });
  }, [currentQuestionId, currentSectionKey, sectionTrees, completedQuestions]);

  const toggleSection = useCallback((sectionKey) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  }, []);

  const toggleNode = useCallback((nodeId) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const navigateTo = useCallback(
    (sectionKey, questionId, itemIndex = null) => {
      setNavigationHistory((prev) => [
        ...prev,
        {
          sectionKey: currentSectionKey,
          questionId: currentQuestionId,
          itemIndex: currentItemIndex,
        },
      ]);
      setCurrentSectionKey(sectionKey);
      setCurrentQuestionId(questionId);
      if (itemIndex !== null) {
        setCurrentItemIndex(itemIndex);
      }
    },
    [currentSectionKey, currentQuestionId, currentItemIndex],
  );

  const goBack = useCallback(() => {
    if (navigationHistory.length === 0) return;
    const prev = navigationHistory[navigationHistory.length - 1];
    setNavigationHistory((h) => h.slice(0, -1));
    setCurrentSectionKey(prev.sectionKey);
    setCurrentQuestionId(prev.questionId);
    if (prev.itemIndex !== undefined) {
      setCurrentItemIndex(prev.itemIndex);
    }
  }, [navigationHistory]);

  // Go back to item manager (for looped sections)
  const goToItemManager = useCallback(() => {
    setCurrentQuestionId(null);
    setCurrentItemIndex(null);
    setNavigationHistory([]);
  }, []);

  // Get answer key based on whether we're in a looped section
  const getAnswerKey = useCallback(
    (field) => {
      if (isLoopedSection && currentItemIndex !== null) {
        return `${currentSectionKey}.${currentItemIndex}.${currentQuestionId}.${field}`;
      }
      return `${currentSectionKey}.${currentQuestionId}.${field}`;
    },
    [currentSectionKey, currentQuestionId, currentItemIndex, isLoopedSection],
  );

  const saveFieldValue = useCallback(
    (field, value) => {
      const answerKey = getAnswerKey(field);
      const newAnswers = { ...answers, [answerKey]: value };
      setAnswers(newAnswers);
      saveAnswersToDatabase(newAnswers);

      // Also update the item's answers if in looped section
      if (isLoopedSection && currentItemIndex !== null) {
        setSectionItems((prev) => {
          const items = [...(prev[currentSectionKey] || [])];
          if (items[currentItemIndex]) {
            items[currentItemIndex] = {
              ...items[currentItemIndex],
              answers: {
                ...items[currentItemIndex].answers,
                [`${currentQuestionId}.${field}`]: value,
              },
            };
          }
          return { ...prev, [currentSectionKey]: items };
        });
      }
    },
    [
      getAnswerKey,
      answers,
      saveAnswersToDatabase,
      isLoopedSection,
      currentItemIndex,
      currentSectionKey,
      currentQuestionId,
    ],
  );

  // Get the name field key for a section
  const getNameFieldKey = useCallback((sectionKey) => {
    const nameFields = {
      ponds: "pond_name",
      curb: "curb_type_name",
      walls: "wall_name_location",
      "ada-ramps": "ramp_type",
    };
    return nameFields[sectionKey] || "name";
  }, []);

  // Item Manager handlers
  const handleAddItem = useCallback(
    (itemName) => {
      const items = sectionItems[currentSectionKey] || [];
      const newIndex = items.length;
      const nameField = getNameFieldKey(currentSectionKey);

      // Create the item with the name already set
      const newItem = {
        id: `item-${Date.now()}`,
        answers: {
          [`${nameField}.${nameField}`]: itemName,
        },
        completed: false,
      };

      setSectionItems((prev) => ({
        ...prev,
        [currentSectionKey]: [...(prev[currentSectionKey] || []), newItem],
      }));

      // Also save the name to the main answers object
      const nameAnswerKey = `${currentSectionKey}.${newIndex}.${nameField}.${nameField}`;
      const newAnswers = { ...answers, [nameAnswerKey]: itemName };
      setAnswers(newAnswers);
      saveAnswersToDatabase(newAnswers);

      // Go to the first question after the name question (skip the name question)
      setCurrentItemIndex(newIndex);
      const firstQuestion = getFirstLoopQuestion(currentSectionKey);
      // Find the question after the name question
      const currentSection = config?.sections?.[currentSectionKey];
      const nameQuestion = currentSection?.questions?.[nameField];
      const nextQuestion = nameQuestion?.next || firstQuestion;
      setCurrentQuestionId(nextQuestion);
      setNavigationHistory([]);
    },
    [
      currentSectionKey,
      sectionItems,
      getFirstLoopQuestion,
      getNameFieldKey,
      answers,
      saveAnswersToDatabase,
      config,
    ],
  );

  // Update an item's name
  const handleUpdateItemName = useCallback(
    (index, newName) => {
      const nameField = getNameFieldKey(currentSectionKey);

      // Update in sectionItems
      setSectionItems((prev) => {
        const items = [...(prev[currentSectionKey] || [])];
        if (items[index]) {
          items[index] = {
            ...items[index],
            answers: {
              ...items[index].answers,
              [`${nameField}.${nameField}`]: newName,
            },
          };
        }
        return { ...prev, [currentSectionKey]: items };
      });

      // Update in main answers
      const nameAnswerKey = `${currentSectionKey}.${index}.${nameField}.${nameField}`;
      const newAnswers = { ...answers, [nameAnswerKey]: newName };
      setAnswers(newAnswers);
      saveAnswersToDatabase(newAnswers);
    },
    [currentSectionKey, getNameFieldKey, answers, saveAnswersToDatabase],
  );

  const handleEditItem = useCallback(
    (index) => {
      setCurrentItemIndex(index);
      const firstQuestion = getFirstLoopQuestion(currentSectionKey);
      setCurrentQuestionId(firstQuestion);
      setNavigationHistory([]);
    },
    [currentSectionKey, getFirstLoopQuestion],
  );

  const handleDeleteItem = useCallback(
    (index) => {
      setSectionItems((prev) => {
        const items = [...(prev[currentSectionKey] || [])];
        items.splice(index, 1);
        return { ...prev, [currentSectionKey]: items };
      });

      // Also remove answers for this item from the main answers object
      setAnswers((prev) => {
        const newAnswers = { ...prev };
        const prefix = `${currentSectionKey}.${index}.`;
        Object.keys(newAnswers).forEach((key) => {
          if (key.startsWith(prefix)) {
            delete newAnswers[key];
          }
        });
        // Re-index answers for items after the deleted one
        const items = sectionItems[currentSectionKey] || [];
        for (let i = index + 1; i < items.length; i++) {
          const oldPrefix = `${currentSectionKey}.${i}.`;
          const newPrefix = `${currentSectionKey}.${i - 1}.`;
          Object.keys(newAnswers).forEach((key) => {
            if (key.startsWith(oldPrefix)) {
              const newKey = key.replace(oldPrefix, newPrefix);
              newAnswers[newKey] = newAnswers[key];
              delete newAnswers[key];
            }
          });
        }
        saveAnswersToDatabase(newAnswers);
        return newAnswers;
      });
    },
    [currentSectionKey, sectionItems, saveAnswersToDatabase],
  );

  const handleContinueFromItemManager = useCallback(() => {
    const currentIdx = sectionKeys.indexOf(currentSectionKey);
    if (currentIdx < sectionKeys.length - 1) {
      const nextSectionKey = sectionKeys[currentIdx + 1];
      const nextSection = config?.sections?.[nextSectionKey];
      const nextIsLooped = loopedSections.has(nextSectionKey);

      setCurrentSectionKey(nextSectionKey);
      setCurrentItemIndex(null);
      setNavigationHistory([]);

      if (nextIsLooped) {
        setCurrentQuestionId(null); // Go to item manager
      } else {
        setCurrentQuestionId(nextSection?.initialQuestion || "");
      }
    }
  }, [sectionKeys, currentSectionKey, config, loopedSections]);

  const handleBackFromItemManager = useCallback(() => {
    const currentIdx = sectionKeys.indexOf(currentSectionKey);
    if (currentIdx > 0) {
      const prevSectionKey = sectionKeys[currentIdx - 1];
      const prevSection = config?.sections?.[prevSectionKey];
      const prevIsLooped = loopedSections.has(prevSectionKey);

      setCurrentSectionKey(prevSectionKey);
      setCurrentItemIndex(null);
      setNavigationHistory([]);

      if (prevIsLooped) {
        setCurrentQuestionId(null); // Go to item manager
      } else {
        setCurrentQuestionId(prevSection?.initialQuestion || "");
      }
    }
  }, [sectionKeys, currentSectionKey, config, loopedSections]);

  // Get item name for display in ItemManager (from item.answers)
  const getItemName = useCallback(
    (item, index) => {
      const nameFields = {
        ponds: ["pond_name.pond_name", "pond_name"],
        curb: ["curb_type_name.curb_type_name", "curb_type_name"],
        walls: ["wall_name_location.wall_name_location", "wall_name_location"],
        "ada-ramps": ["ramp_type.ramp_type", "ramp_type"],
      };
      const fields = nameFields[currentSectionKey] || [];
      for (const field of fields) {
        if (item.answers[field]) {
          return item.answers[field];
        }
      }
      return null;
    },
    [currentSectionKey],
  );

  const handleAnswer = useCallback(
    (field, value) => {
      let newAnswers = answers;
      if (field !== "_navigate") {
        const answerKey = getAnswerKey(field);
        newAnswers = { ...answers, [answerKey]: value };
        setAnswers(newAnswers);
        saveAnswersToDatabase(newAnswers);

        // Also update item answers if in looped section
        if (isLoopedSection && currentItemIndex !== null) {
          setSectionItems((prev) => {
            const items = [...(prev[currentSectionKey] || [])];
            if (items[currentItemIndex]) {
              items[currentItemIndex] = {
                ...items[currentItemIndex],
                answers: {
                  ...items[currentItemIndex].answers,
                  [`${currentQuestionId}.${field}`]: value,
                },
              };
            }
            return { ...prev, [currentSectionKey]: items };
          });
        }
      }

      const questionKey =
        isLoopedSection && currentItemIndex !== null
          ? `${currentSectionKey}.${currentItemIndex}.${currentQuestionId}`
          : `${currentSectionKey}.${currentQuestionId}`;
      setCompletedQuestions((prev) => new Set([...prev, questionKey]));

      const question = currentSection?.questions?.[currentQuestionId];
      if (!question) return;

      let nextId = null;

      if (field === "_navigate" && value) {
        nextId = value;
      } else if (
        question.type === "boolean" ||
        question.type === "manager_decision"
      ) {
        nextId =
          value === true || value === "yes" ? question.onYes : question.onNo;
      } else {
        nextId = question.next;
      }

      if (!nextId) return;

      if (NAV_TOKENS.includes(nextId)) {
        // For looped sections, LOOP_END means go back to item manager (not next section)
        if (
          nextId === "LOOP_END" ||
          nextId === "LOOP_CONTINUE" ||
          nextId === "ADA_LOOP_CONTINUE"
        ) {
          if (isLoopedSection) {
            // Mark current item as complete
            setSectionItems((prev) => {
              const items = [...(prev[currentSectionKey] || [])];
              if (items[currentItemIndex]) {
                items[currentItemIndex] = {
                  ...items[currentItemIndex],
                  completed: true,
                };
              }
              return { ...prev, [currentSectionKey]: items };
            });
            // Go back to item manager
            goToItemManager();
            return;
          }
        }

        if (nextId === "SECTION_COMPLETE") {
          const currentIdx = sectionKeys.indexOf(currentSectionKey);
          if (currentIdx < sectionKeys.length - 1) {
            const nextSectionKey = sectionKeys[currentIdx + 1];
            const nextSection = config?.sections?.[nextSectionKey];
            const nextIsLooped = loopedSections.has(nextSectionKey);

            setCurrentSectionKey(nextSectionKey);
            setCurrentItemIndex(null);
            setNavigationHistory([]);

            if (nextIsLooped) {
              setCurrentQuestionId(null);
            } else {
              setCurrentQuestionId(nextSection?.initialQuestion || "");
            }
          }
          return;
        }

        if (
          nextId === "LOOP_START_SINGLE" ||
          nextId === "LOOP_START" ||
          nextId === "ADA_LOOP_START_SINGLE"
        ) {
          // This shouldn't happen with the new flow, but handle it just in case
          const firstQ = getFirstLoopQuestion(currentSectionKey);
          if (firstQ) {
            navigateTo(currentSectionKey, firstQ);
          }
          return;
        }
        return;
      }

      if (currentSection?.questions?.[nextId]) {
        navigateTo(currentSectionKey, nextId);
      }
    },
    [
      currentSectionKey,
      currentQuestionId,
      currentSection,
      sectionKeys,
      config,
      navigateTo,
      answers,
      saveAnswersToDatabase,
      getAnswerKey,
      isLoopedSection,
      currentItemIndex,
      loopedSections,
      goToItemManager,
      getFirstLoopQuestion,
    ],
  );

  const getCurrentAnswer = useCallback(
    (field) => {
      const answerKey = getAnswerKey(field);
      return answers[answerKey];
    },
    [getAnswerKey, answers],
  );

  // Check if export is enabled (at least one section has items)
  const isExportEnabled = useCallback(() => {
    return Object.values(sectionItems).some(
      (items) => Array.isArray(items) && items.length > 0,
    );
  }, [sectionItems]);

  // Build sessionData for export from sectionItems
  const buildSessionData = useCallback(() => {
    const sessionData = {};
    const sectionDataKeyMap = {
      ponds: "ponds",
      grading: "gradingSections",
      curb: "curbTypes",
      walls: "walls",
      ditches_swales: "ditchesSwales",
      sidewalks: "sidewalks",
      ada_ramps: "adaRamps",
      building_pads: "buildingPads",
      grate_elevations: "grateElevations",
    };

    for (const [sectionKey, items] of Object.entries(sectionItems)) {
      if (!items || items.length === 0) continue;
      const dataKey = sectionDataKeyMap[sectionKey] || sectionKey;

      sessionData[dataKey] = items.map((item) => {
        // Flatten item.answers to get field values at top level
        const flattened = { ...item };
        if (item.answers) {
          for (const [key, value] of Object.entries(item.answers)) {
            // Keys are like "questionId.field" - extract the last part as field name
            const parts = key.split(".");
            const fieldName = parts[parts.length - 1];
            flattened[fieldName] = value;
          }
        }
        return flattened;
      });
    }
    return sessionData;
  }, [sectionItems]);

  // Handle export
  const handleExport = useCallback(
    async (notes) => {
      try {
        const sessionData = buildSessionData();
        await modelPlannerExportService.generatePDF(
          sessionData,
          project,
          notes,
        );
      } catch (error) {
        console.error("Export error:", error);
        throw error;
      }
    },
    [buildSessionData, project],
  );

  const projectLabel = project
    ? `${formatJobRevNumber(project.job_number, project.rev)} - ${project.name}`
    : `Project ${projectId}`;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--dark-bg)",
        display: "flex",
      }}
    >
      {/* Sidebar */}
      <div
        style={{
          width: sidebarCollapsed ? 50 : 280,
          background: "var(--dark-surface)",
          borderRight: "1px solid var(--gray-700)",
          display: "flex",
          flexDirection: "column",
          transition: "width 0.2s",
        }}
      >
        <div
          style={{
            padding: "var(--space-3)",
            borderBottom: "1px solid var(--gray-700)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {!sidebarCollapsed && (
            <span
              style={{
                color: "var(--gray-300)",
                fontWeight: 500,
                fontSize: "var(--text-sm)",
              }}
            >
              Sections
            </span>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{
              background: "none",
              border: "none",
              color: "var(--gray-400)",
              cursor: "pointer",
              padding: 4,
            }}
          >
            {sidebarCollapsed ? (
              <ChevronRight size={16} />
            ) : (
              <ChevronLeft size={16} />
            )}
          </button>
        </div>

        {!sidebarCollapsed && (
          <div style={{ flex: 1, overflow: "auto", padding: "var(--space-2)" }}>
            {sectionKeys.map((sectionKey) => {
              const section = config?.sections?.[sectionKey];
              const isActiveSection = sectionKey === currentSectionKey;
              const tree = sectionTrees[sectionKey] || [];
              const isExpanded = expandedSections.has(sectionKey);
              const isLooped = loopedSections.has(sectionKey);
              const itemCount = (sectionItems[sectionKey] || []).length;
              const progress = getSectionProgress(sectionKey);
              const isSectionComplete =
                progress.total > 0 && progress.completed === progress.total;

              return (
                <div
                  key={sectionKey}
                  style={{ marginBottom: "var(--space-2)" }}
                >
                  <div
                    style={{
                      width: "100%",
                      padding: "var(--space-2)",
                      background: isActiveSection
                        ? "rgba(59, 130, 246, 0.15)"
                        : "transparent",
                      borderRadius: "var(--radius-md)",
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-1)",
                      color: isActiveSection
                        ? "var(--blue-300)"
                        : "var(--gray-400)",
                    }}
                  >
                    {/* Chevron - toggles expand/collapse only */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSection(sectionKey);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 2,
                        cursor: "pointer",
                        color: "inherit",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ChevronDown
                        size={14}
                        style={{
                          transform: isExpanded
                            ? "rotate(0deg)"
                            : "rotate(-90deg)",
                          transition: "transform 0.15s",
                        }}
                      />
                    </button>
                    {/* Section name - navigates to section/item manager */}
                    <button
                      onClick={() => {
                        setCurrentSectionKey(sectionKey);
                        setCurrentItemIndex(null);
                        setNavigationHistory([]);
                        if (isLooped) {
                          setCurrentQuestionId(null); // Item manager mode
                        } else {
                          setCurrentQuestionId(section?.initialQuestion || "");
                        }
                        // Also expand if not already
                        if (!isExpanded) {
                          toggleSection(sectionKey);
                        }
                      }}
                      style={{
                        flex: 1,
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        textAlign: "left",
                        color: "inherit",
                        fontWeight: isActiveSection ? 500 : 400,
                        fontSize: "var(--text-sm)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {section?.label || sectionKey}
                    </button>
                    <span
                      style={{
                        fontSize: "10px",
                        padding: "1px 6px",
                        borderRadius: "var(--radius-sm)",
                        background: isSectionComplete
                          ? "rgba(34, 197, 94, 0.15)"
                          : progress.completed > 0
                            ? "rgba(59, 130, 246, 0.1)"
                            : "transparent",
                        color: isSectionComplete
                          ? "var(--green-400)"
                          : progress.completed > 0
                            ? "var(--blue-400)"
                            : "var(--gray-500)",
                        flexShrink: 0,
                      }}
                    >
                      {isLooped
                        ? itemCount > 0
                          ? `${progress.completed}/${itemCount}`
                          : "0"
                        : `${progress.completed}/${progress.total}`}
                    </span>
                  </div>

                  {isExpanded && (
                    <div
                      style={{
                        paddingLeft: "var(--space-4)",
                        marginTop: "var(--space-1)",
                      }}
                    >
                      {isLooped ? (
                        // Looped sections: show items, and question tree when editing
                        <>
                          {(sectionItems[sectionKey] || []).map(
                            (item, itemIndex) => {
                              const itemKey = `${sectionKey}.${itemIndex}`;
                              const itemName = getItemDisplayName(
                                sectionKey,
                                itemIndex,
                              );
                              const isEditingThisItem =
                                isActiveSection &&
                                currentItemIndex === itemIndex;
                              // Build item-specific tree starting from loop questions (skip "has_curb" etc.)
                              const itemTree = isEditingThisItem
                                ? buildNavigationTree(
                                    section,
                                    answers,
                                    sectionKey,
                                    completedQuestions,
                                    itemIndex,
                                    true,
                                  )
                                : [];

                              // Handler to start editing this item
                              const startEditingItem = () => {
                                setCurrentSectionKey(sectionKey);
                                setCurrentItemIndex(itemIndex);
                                setCurrentQuestionId(
                                  getFirstLoopQuestion(sectionKey),
                                );
                                setNavigationHistory([]);
                              };

                              return (
                                <div key={itemKey}>
                                  <button
                                    onClick={startEditingItem}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "var(--space-2)",
                                      padding: "var(--space-1) var(--space-2)",
                                      background: isEditingThisItem
                                        ? "rgba(59, 130, 246, 0.2)"
                                        : "transparent",
                                      borderRadius: "var(--radius-sm)",
                                      marginBottom: 2,
                                      width: "100%",
                                      border: "none",
                                      cursor: "pointer",
                                      textAlign: "left",
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: "var(--gray-600)",
                                        fontSize: "10px",
                                      }}
                                    >
                                      {itemIndex + 1}.
                                    </span>
                                    <span
                                      style={{
                                        flex: 1,
                                        fontSize: "var(--text-xs)",
                                        color: isEditingThisItem
                                          ? "var(--blue-300)"
                                          : "var(--gray-400)",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {itemName ||
                                        `${section?.label || sectionKey} ${itemIndex + 1}`}
                                    </span>
                                    {item.completed && (
                                      <Check
                                        size={10}
                                        style={{
                                          color: "var(--green-400)",
                                          flexShrink: 0,
                                        }}
                                      />
                                    )}
                                  </button>
                                  {/* Show question tree when editing this item */}
                                  {isEditingThisItem && itemTree.length > 0 && (
                                    <div
                                      style={{
                                        paddingLeft: "var(--space-3)",
                                        marginTop: 2,
                                        marginBottom: "var(--space-2)",
                                      }}
                                    >
                                      {itemTree.map((node, i) => (
                                        <TreeNode
                                          key={`${node.id}-${i}`}
                                          node={node}
                                          currentQuestionId={currentQuestionId}
                                          expandedNodes={expandedNodes}
                                          onToggleExpand={toggleNode}
                                          onNavigate={(qId) =>
                                            navigateTo(sectionKey, qId)
                                          }
                                          completedQuestions={
                                            completedQuestions
                                          }
                                          sectionKey={sectionKey}
                                          itemIndex={itemIndex}
                                          pathToCurrentQuestion={
                                            pathToCurrentQuestion
                                          }
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            },
                          )}
                          {/* Show message if no items */}
                          {(sectionItems[sectionKey] || []).length === 0 && (
                            <div
                              style={{
                                fontSize: "var(--text-xs)",
                                color: "var(--gray-500)",
                                padding: "var(--space-1) var(--space-2)",
                                fontStyle: "italic",
                              }}
                            >
                              No items yet
                            </div>
                          )}
                        </>
                      ) : (
                        // Non-looped sections: show question tree
                        tree.length > 0 &&
                        tree.map((node, i) => (
                          <TreeNode
                            key={`${node.id}-${i}`}
                            node={node}
                            currentQuestionId={
                              isActiveSection ? currentQuestionId : null
                            }
                            expandedNodes={expandedNodes}
                            onToggleExpand={toggleNode}
                            onNavigate={(qId) => navigateTo(sectionKey, qId)}
                            completedQuestions={completedQuestions}
                            sectionKey={sectionKey}
                            pathToCurrentQuestion={
                              isActiveSection ? pathToCurrentQuestion : []
                            }
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div
          style={{
            padding: "var(--space-3) var(--space-4)",
            borderBottom: "1px solid var(--gray-700)",
            background: "var(--dark-surface)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              color: "var(--gray-300)",
              display: "flex",
              alignItems: "center",
              gap: "var(--space-3)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <button
                onClick={() => {
                  if (isLoopedSection) {
                    setCurrentQuestionId(null);
                    setCurrentItemIndex(null);
                  } else {
                    setCurrentQuestionId(currentSection?.initialQuestion || "");
                  }
                  setNavigationHistory([]);
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--gray-400)",
                  cursor: "pointer",
                  padding: "2px 4px",
                  borderRadius: "var(--radius-sm)",
                }}
                title="Go to section start"
              >
                {currentSection?.label || currentSectionKey}
              </button>
              {isInItemManagerMode ? (
                <span style={{ color: "var(--gray-500)", marginLeft: 4 }}>
                  (Item Manager)
                </span>
              ) : (
                <>
                  <span style={{ color: "var(--gray-600)", margin: "0 4px" }}>
                    /
                  </span>
                  {currentItemIndex !== null && (
                    <>
                      <button
                        onClick={() => {
                          const firstQuestion =
                            getFirstLoopQuestion(currentSectionKey);
                          setCurrentQuestionId(firstQuestion);
                          setNavigationHistory([]);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--blue-400)",
                          cursor: "pointer",
                          padding: "2px 4px",
                          borderRadius: "var(--radius-sm)",
                        }}
                        title="Go to item start"
                      >
                        {getItemDisplayName(
                          currentSectionKey,
                          currentItemIndex,
                        ) || `Item ${currentItemIndex + 1}`}
                      </button>
                      <span
                        style={{ color: "var(--gray-600)", margin: "0 4px" }}
                      >
                        /
                      </span>
                    </>
                  )}
                  <span style={{ color: "var(--gray-300)" }}>
                    {currentQuestion?.text?.slice(0, 40) || currentQuestionId}
                    {currentQuestion?.text?.length > 40 ? "..." : ""}
                  </span>
                </>
              )}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: "var(--text-xs)",
                color:
                  saveStatus === "saving"
                    ? "var(--amber-400)"
                    : saveStatus === "saved"
                      ? "var(--green-400)"
                      : saveStatus === "error"
                        ? "var(--red-400)"
                        : "var(--gray-500)",
              }}
            >
              {saveStatus === "saving" && (
                <>
                  <Cloud size={12} /> Saving...
                </>
              )}
              {saveStatus === "saved" && (
                <>
                  <Cloud size={12} /> Saved
                </>
              )}
              {saveStatus === "error" && (
                <>
                  <CloudOff size={12} /> Save failed
                </>
              )}
              {saveStatus === "idle" && session?.id && (
                <>
                  <Cloud size={12} style={{ opacity: 0.5 }} />
                </>
              )}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-3)",
            }}
          >
            <span
              style={{ color: "var(--gray-400)", fontSize: "var(--text-sm)" }}
            >
              {projectLabel}
            </span>
            <div
              style={{
                display: "flex",
                gap: "var(--space-2)",
                alignItems: "center",
              }}
            >
              <ExportButton
                sessionData={buildSessionData()}
                project={project}
                disabled={!isExportEnabled()}
                onExport={handleExport}
              />
              <button
                onClick={() => window.close()}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-1)",
                  padding: "var(--space-2) var(--space-3)",
                  background: "var(--gray-700)",
                  border: "none",
                  borderRadius: "var(--radius-md)",
                  color: "var(--gray-300)",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>

        {/* Question Content */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            padding: "var(--space-8)",
            display: "flex",
            justifyContent: "center",
            alignItems: isInItemManagerMode ? "flex-start" : "flex-start",
          }}
        >
          <div style={{ maxWidth: 700, width: "100%" }}>
            {isInItemManagerMode ? (
              <ItemManager
                sectionKey={currentSectionKey}
                sectionLabel={currentSection?.label || currentSectionKey}
                items={sectionItems[currentSectionKey] || []}
                onAddItem={handleAddItem}
                onEditItem={handleEditItem}
                onDeleteItem={handleDeleteItem}
                onUpdateItemName={handleUpdateItemName}
                onContinue={handleContinueFromItemManager}
                onBack={handleBackFromItemManager}
                canGoBack={sectionKeys.indexOf(currentSectionKey) > 0}
                getItemName={getItemName}
              />
            ) : currentQuestion?.type === "manager_review_panel" ? (
              <ManagerReviewPanel
                flags={computeFlags(sectionItems, answers)}
                decisions={answers}
                onDecision={(field, value) => {
                  const newAnswers = { ...answers, [field]: value };
                  setAnswers(newAnswers);
                  saveAnswersToDatabase(newAnswers);
                }}
                onContinue={() => handleAnswer("_navigate", currentQuestion.next)}
                onBack={
                  sectionKeys.indexOf(currentSectionKey) > 0
                    ? () => {
                        const prevIdx = sectionKeys.indexOf(currentSectionKey) - 1;
                        const prevSectionKey = sectionKeys[prevIdx];
                        const prevSection = config?.sections?.[prevSectionKey];
                        setCurrentSectionKey(prevSectionKey);
                        setCurrentQuestionId(prevSection?.initialQuestion || "");
                        setCurrentItemIndex(null);
                        setNavigationHistory([]);
                      }
                    : null
                }
                canGoBack={sectionKeys.indexOf(currentSectionKey) > 0}
              />
            ) : currentQuestion ? (
              <QuestionCard
                question={currentQuestion}
                questionId={currentQuestionId}
                onAnswer={handleAnswer}
                onSaveField={saveFieldValue}
                getAnswer={getCurrentAnswer}
                onBack={
                  navigationHistory.length > 0
                    ? goBack
                    : isLoopedSection
                      ? goToItemManager
                      : goBack
                }
                canGoBack={isLoopedSection || navigationHistory.length > 0}
                showBackToList={isLoopedSection && currentItemIndex !== null}
                onBackToList={goToItemManager}
                sectionLabel={currentSection?.label}
              />
            ) : (
              <div
                style={{
                  textAlign: "center",
                  color: "var(--gray-400)",
                  padding: "var(--space-8)",
                }}
              >
                Question "{currentQuestionId}" not found
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "var(--space-3) var(--space-4)",
            borderTop: "1px solid var(--gray-700)",
            background: "var(--dark-surface)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: "var(--text-sm)",
            color: "var(--gray-500)",
          }}
        >
          <div>
            {isInItemManagerMode ? (
              <>
                {Object.values(sectionItems).flat().length} items total •{" "}
                {Object.keys(answers).length} answers recorded
              </>
            ) : (
              <>
                Step {navigationHistory.length + 1} •{" "}
                {currentItemIndex !== null
                  ? `Editing Item ${currentItemIndex + 1} • `
                  : ""}
                {Object.keys(answers).length} answers recorded
              </>
            )}
          </div>
          <div>Model Planner</div>
        </div>
      </div>
    </div>
  );
};

/**
 * QuestionCard - Renders a question with input controls
 */
const QuestionCard = ({
  question,
  questionId,
  onAnswer,
  onSaveField,
  getAnswer,
  onBack,
  canGoBack,
  showBackToList,
  onBackToList,
  sectionLabel,
}) => {
  const isBoolean =
    question.type === "boolean" || question.type === "manager_decision";

  const [formValues, setFormValues] = useState({});
  const [validationError, setValidationError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    setValidationError(null);
    if (question.type === "grouped_form" && question.fields) {
      const savedValues = {};
      question.fields.forEach((field) => {
        const savedValue = getAnswer(field.field);
        if (savedValue !== undefined) {
          savedValues[field.field] = savedValue;
        }
        // Also load N/A checkbox state for elevation fields
        if (field.type === "elevation" && field.allowNA) {
          const naValue = getAnswer(`${field.field}_na`);
          if (naValue !== undefined) {
            savedValues[`${field.field}_na`] = naValue;
          }
        }
      });
      setFormValues(savedValues);
    } else {
      setFormValues({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId]); // Only re-initialize when question changes, not on every getAnswer update

  const handleFormFieldChange = (fieldName, value) => {
    setFormValues((prev) => ({ ...prev, [fieldName]: value }));
    setValidationError(null);
    onSaveField(fieldName, value);
  };

  const validateGroupedForm = () => {
    if (question.type !== "grouped_form" || !question.fields) return true;
    const requiredFields = question.fields.filter((f) => f.required !== false);
    for (const field of requiredFields) {
      const value = formValues[field.field];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        const fieldLabel = field.text || field.field;
        if (field.type === "image") {
          setValidationError(`Please paste a screenshot for: ${fieldLabel}`);
        } else {
          setValidationError(`Please fill in: ${fieldLabel}`);
        }
        return false;
      }
    }
    return true;
  };

  const handleContinueClick = () => {
    if (validateGroupedForm()) {
      onAnswer("_navigate", question.next);
    }
  };

  // Keyboard shortcuts: Y/N for boolean, Escape to go back
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't capture when typing in inputs
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
        return;

      if (isBoolean) {
        if (e.key === "y" || e.key === "Y") {
          e.preventDefault();
          onAnswer(question.field || questionId, true);
        } else if (e.key === "n" || e.key === "N") {
          e.preventDefault();
          onAnswer(question.field || questionId, false);
        }
      }

      if (e.key === "Escape" && canGoBack) {
        e.preventDefault();
        onBack();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isBoolean, question.field, questionId, onAnswer, onBack, canGoBack]);

  const BackButton = () =>
    canGoBack ? (
      <button
        onClick={onBack}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-1)",
          padding: "var(--space-3) var(--space-4)",
          background: "var(--gray-700)",
          border: "none",
          borderRadius: "var(--radius-md)",
          color: "var(--gray-300)",
          cursor: "pointer",
        }}
      >
        <ChevronLeft size={16} />
        Back
      </button>
    ) : null;

  return (
    <div
      style={{
        background: "var(--dark-surface)",
        border: "1px solid var(--gray-700)",
        borderRadius: "var(--radius-lg)",
        padding: "var(--space-6)",
      }}
    >
      {/* Back to List button for looped sections */}
      {showBackToList && (
        <button
          onClick={onBackToList}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-1)",
            padding: "var(--space-2)",
            background: "transparent",
            border: "none",
            color: "var(--blue-400)",
            cursor: "pointer",
            fontSize: "var(--text-sm)",
            marginBottom: "var(--space-3)",
            marginLeft: "-4px",
          }}
        >
          <ChevronLeft size={14} />
          Back to {sectionLabel} list
        </button>
      )}

      <div style={{ marginBottom: "var(--space-4)" }}>
        <h2
          style={{
            margin: 0,
            fontSize: "var(--text-xl)",
            fontWeight: 500,
            color: "var(--gray-100)",
          }}
        >
          {question.text || questionId}
        </h2>
      </div>

      <div style={{ marginTop: "var(--space-4)" }}>
        {isBoolean ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-3)",
            }}
          >
            <div style={{ display: "flex", gap: "var(--space-3)" }}>
              {canGoBack && <BackButton />}
              <button
                onClick={() => onAnswer(question.field || questionId, true)}
                style={{
                  flex: 1,
                  padding: "var(--space-4)",
                  background:
                    getAnswer(question.field || questionId) === true
                      ? "#15803d"
                      : "#374151",
                  border:
                    getAnswer(question.field || questionId) === true
                      ? "2px solid #22c55e"
                      : "2px solid transparent",
                  borderRadius: "var(--radius-md)",
                  color: "white",
                  fontSize: "var(--text-lg)",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Yes
              </button>
              <button
                onClick={() => onAnswer(question.field || questionId, false)}
                style={{
                  flex: 1,
                  padding: "var(--space-4)",
                  background:
                    getAnswer(question.field || questionId) === false
                      ? "#991b1b"
                      : "#374151",
                  border:
                    getAnswer(question.field || questionId) === false
                      ? "2px solid #ef4444"
                      : "2px solid transparent",
                  borderRadius: "var(--radius-md)",
                  color: "white",
                  fontSize: "var(--text-lg)",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                No
              </button>
            </div>
          </div>
        ) : question.type === "text" || question.type === "elevation" ? (
          <div>
            <input
              type={question.type === "elevation" ? "number" : "text"}
              placeholder={question.placeholder || "Enter value..."}
              defaultValue={getAnswer(question.field || questionId) || ""}
              key={questionId}
              autoFocus
              style={{
                width: "100%",
                padding: "var(--space-3)",
                background: "var(--gray-800)",
                border: "1px solid var(--gray-600)",
                borderRadius: "var(--radius-md)",
                color: "var(--gray-100)",
                fontSize: "var(--text-base)",
              }}
              onBlur={(e) => {
                // Save when user leaves the field
                onSaveField(question.field || questionId, e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onAnswer(question.field || questionId, e.target.value);
                }
              }}
            />
            <p
              style={{
                margin: "var(--space-2) 0 0",
                fontSize: "var(--text-xs)",
                color: "var(--gray-500)",
              }}
            >
              Press Enter to continue
            </p>
            {canGoBack && (
              <div style={{ marginTop: "var(--space-3)" }}>
                <BackButton />
              </div>
            )}
          </div>
        ) : question.type === "choice" && question.options ? (
          (() => {
            const currentAnswer = getAnswer(question.field || questionId);
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-2)",
                }}
              >
                {question.options.map((opt, i) => {
                  const optValue = opt.value || opt;
                  const isSelected = currentAnswer === optValue;
                  return (
                    <button
                      key={i}
                      onClick={() =>
                        onAnswer(question.field || questionId, optValue)
                      }
                      style={{
                        padding: "var(--space-3)",
                        background: isSelected
                          ? "rgba(59, 130, 246, 0.2)"
                          : "var(--gray-700)",
                        border: isSelected
                          ? "2px solid var(--blue-500)"
                          : "2px solid transparent",
                        borderRadius: "var(--radius-md)",
                        color: isSelected
                          ? "var(--blue-300)"
                          : "var(--gray-100)",
                        textAlign: "left",
                        cursor: "pointer",
                        fontWeight: isSelected ? 500 : 400,
                      }}
                    >
                      {opt.label || opt}
                    </button>
                  );
                })}
                {canGoBack && (
                  <div style={{ marginTop: "var(--space-2)" }}>
                    <BackButton />
                  </div>
                )}
              </div>
            );
          })()
        ) : question.type === "grouped_form" ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-3)",
            }}
          >
            {question.fields?.map((field, i) => (
              <div key={i}>
                <label
                  style={{
                    display: "block",
                    fontSize: "var(--text-sm)",
                    color: "var(--gray-300)",
                    marginBottom: 4,
                  }}
                >
                  {field.text || field.field}
                  {field.required !== false && (
                    <span style={{ color: "var(--red-400)", marginLeft: 4 }}>
                      *
                    </span>
                  )}
                </label>
                {field.type === "image" ? (
                  <div
                    tabIndex={0}
                    onPaste={(e) => {
                      const items = e.clipboardData?.items;
                      if (items) {
                        for (const item of items) {
                          if (item.type.startsWith("image/")) {
                            const file = item.getAsFile();
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                handleFormFieldChange(
                                  field.field,
                                  event.target.result,
                                );
                              };
                              reader.readAsDataURL(file);
                            }
                            break;
                          }
                        }
                      }
                    }}
                    style={{
                      padding: "var(--space-4)",
                      background: formValues[field.field]
                        ? "rgba(34, 197, 94, 0.1)"
                        : "var(--gray-800)",
                      border: formValues[field.field]
                        ? "2px solid #22c55e"
                        : "2px dashed var(--gray-600)",
                      borderRadius: "var(--radius-md)",
                      textAlign: "center",
                      color: formValues[field.field]
                        ? "#22c55e"
                        : "var(--gray-500)",
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    {formValues[field.field]?.startsWith("data:image") ? (
                      <div style={{ position: "relative" }}>
                        <img
                          src={formValues[field.field]}
                          alt="Pasted screenshot"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "200px",
                            borderRadius: "var(--radius-sm)",
                            cursor: "zoom-in",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setLightboxImage(formValues[field.field]);
                          }}
                        />
                        <div
                          style={{
                            fontSize: "var(--text-xs)",
                            color: "var(--gray-400)",
                            marginTop: "var(--space-1)",
                          }}
                        >
                          Click image to enlarge
                        </div>
                      </div>
                    ) : formValues[field.field] ? (
                      "✓ Screenshot pasted"
                    ) : (
                      "Click here and paste screenshot (Cmd+V)"
                    )}
                  </div>
                ) : field.type === "elevation" ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-2)",
                    }}
                  >
                    <input
                      type="number"
                      placeholder="0.00"
                      value={formValues[field.field] || ""}
                      onChange={(e) =>
                        handleFormFieldChange(field.field, e.target.value)
                      }
                      style={{
                        flex: 1,
                        padding: "var(--space-2)",
                        background: "var(--gray-800)",
                        border: "1px solid var(--gray-600)",
                        borderRadius: "var(--radius-sm)",
                        color: "var(--gray-100)",
                      }}
                    />
                    {field.allowNA && (
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          color: "var(--gray-400)",
                          fontSize: "var(--text-sm)",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formValues[`${field.field}_na`] || false}
                          onChange={(e) => {
                            handleFormFieldChange(
                              `${field.field}_na`,
                              e.target.checked,
                            );
                            if (e.target.checked) {
                              handleFormFieldChange(field.field, "N/A");
                            }
                          }}
                        />{" "}
                        N/A
                      </label>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    placeholder={field.placeholder || ""}
                    value={formValues[field.field] || ""}
                    onChange={(e) =>
                      handleFormFieldChange(field.field, e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "var(--space-2)",
                      background: "var(--gray-800)",
                      border: "1px solid var(--gray-600)",
                      borderRadius: "var(--radius-sm)",
                      color: "var(--gray-100)",
                    }}
                  />
                )}
              </div>
            ))}
            {validationError && (
              <div
                style={{
                  padding: "var(--space-2) var(--space-3)",
                  background: "rgba(239, 68, 68, 0.1)",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                  borderRadius: "var(--radius-md)",
                  color: "var(--red-400)",
                  fontSize: "var(--text-sm)",
                }}
              >
                {validationError}
              </div>
            )}
            <div
              style={{
                display: "flex",
                gap: "var(--space-2)",
                marginTop: "var(--space-2)",
              }}
            >
              {canGoBack && <BackButton />}
              <button
                onClick={handleContinueClick}
                style={{
                  padding: "var(--space-3) var(--space-4)",
                  background: "var(--cta-blue)",
                  border: "none",
                  borderRadius: "var(--radius-md)",
                  color: "white",
                  cursor: "pointer",
                }}
              >
                Continue
              </button>
            </div>
          </div>
        ) : question.type === "multi_checkbox" ? (
          (() => {
            const field = question.field || questionId;
            const currentValue = getAnswer(field) || [];
            const selectedSet = new Set(
              Array.isArray(currentValue) ? currentValue : [],
            );

            const handleToggle = (optValue) => {
              const newSet = new Set(selectedSet);
              if (newSet.has(optValue)) {
                newSet.delete(optValue);
              } else {
                newSet.add(optValue);
              }
              onSaveField(field, Array.from(newSet));
            };

            return (
              <div>
                {(question.options || question.choices || []).map((opt, i) => {
                  const optValue = opt.label || opt;
                  const isChecked = selectedSet.has(optValue);
                  return (
                    <label
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "var(--space-2)",
                        padding: "var(--space-2)",
                        color: "var(--gray-300)",
                        cursor: "pointer",
                        background: isChecked
                          ? "rgba(34, 197, 94, 0.1)"
                          : "transparent",
                        borderRadius: "var(--radius-sm)",
                        marginBottom: "var(--space-1)",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggle(optValue)}
                        style={{
                          accentColor: "var(--cta-blue)",
                          width: 18,
                          height: 18,
                        }}
                      />
                      {optValue}
                    </label>
                  );
                })}
                <div
                  style={{
                    display: "flex",
                    gap: "var(--space-2)",
                    marginTop: "var(--space-3)",
                  }}
                >
                  {canGoBack && <BackButton />}
                  <button
                    onClick={() => onAnswer(field, Array.from(selectedSet))}
                    style={{
                      padding: "var(--space-3) var(--space-4)",
                      background: "var(--cta-blue)",
                      border: "none",
                      borderRadius: "var(--radius-md)",
                      color: "white",
                      cursor: "pointer",
                    }}
                  >
                    Continue
                  </button>
                </div>
              </div>
            );
          })()
        ) : question.type === "confirmation" ? (
          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            {canGoBack && <BackButton />}
            <button
              onClick={() => onAnswer("_navigate", question.next)}
              style={{
                padding: "var(--space-3) var(--space-6)",
                background: "var(--green-600)",
                border: "none",
                borderRadius: "var(--radius-md)",
                color: "white",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Confirm & Continue
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            {canGoBack && <BackButton />}
            <button
              onClick={() => onAnswer("_navigate", question.next)}
              style={{
                padding: "var(--space-3) var(--space-4)",
                background: "var(--cta-blue)",
                border: "none",
                borderRadius: "var(--radius-md)",
                color: "white",
                cursor: "pointer",
              }}
            >
              Continue
            </button>
          </div>
        )}
      </div>

      {lightboxImage && (
        <div
          onClick={() => setLightboxImage(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out",
            padding: "var(--space-4)",
          }}
        >
          <img
            src={lightboxImage}
            alt="Enlarged screenshot"
            style={{
              maxWidth: "95vw",
              maxHeight: "95vh",
              objectFit: "contain",
              borderRadius: "var(--radius-md)",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <div
            style={{
              position: "absolute",
              top: "var(--space-4)",
              right: "var(--space-4)",
              color: "white",
              fontSize: "var(--text-sm)",
              opacity: 0.7,
            }}
          >
            Click anywhere to close
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelPlannerPage;
